import React, { useEffect, useState } from "react";
import { fetchRedeem, markDownloaded, RedeemState } from "./api";

/**
 * Branded gift-redemption landing page (spec §16): company header, hero Winter
 * Panda animation and a download of Winter Desktops' own installer. Clicking
 * download (after accepting the agreements) is what redeems the gift — there is
 * no activation code to type into the app.
 */
export default function RedeemPage({ token }: { token: string }) {
  const [data, setData] = useState<RedeemState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetchRedeem(token);
        if (alive) setData(res);
      } catch {
        if (alive) setData({ success: false, state: "invalid", ms_store_url: "" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-night-fade font-sans text-frost-100">
      {/* aurora glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-aurora-cyan/20 blur-[120px]" />
        <div className="absolute top-10 right-1/4 h-80 w-80 rounded-full bg-aurora-violet/20 blur-[130px]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center px-5 py-16 text-center">
        {loading ? (
          <Spinner />
        ) : !data || data.state === "invalid" ? (
          <StateCard
            title="Invalid invitation"
            body="This invitation link doesn’t look right. Please check the link in your email, or ask the sender to resend it."
          />
        ) : data.state === "expired" ? (
          <StateCard
            title="Invitation expired"
            body="This Winter Panda invitation has expired. Please ask the sender to resend it — they can do that in a click."
          />
        ) : data.state === "redeemed" ? (
          <StateCard
            title="Your Winter Panda is ready 🐼"
            body={`You've already downloaded your gift from ${data.company_name ?? "your sender"}. Need it again, or installing on another PC? Download it below.`}
            footer={
              <div className="mt-6 flex flex-col items-center gap-3">
                <a
                  href={data.download_url || "#"}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-7 py-3 text-sm font-semibold text-midnight-900 shadow-cozy transition-transform hover:-translate-y-0.5"
                >
                  Download again
                </a>
                <a href="/enterprise" className="text-sm text-frost-200/70 hover:text-frost-50">
                  Explore Winter Desktops →
                </a>
              </div>
            }
          />
        ) : (
          <ValidRedeem data={data} token={token} />
        )}
      </div>
    </div>
  );
}

function ValidRedeem({ data, token }: { data: RedeemState; token: string }) {
  // The download stays disabled until the recipient accepts the agreements.
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="reveal is-visible w-full">
      {/* company header */}
      <div className="mb-8">
        {data.logo_url && (
          <img
            src={data.logo_url}
            alt={data.company_name}
            className="mx-auto mb-3 max-h-14 w-auto"
          />
        )}
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-frost-200/55">
          A gift from
        </p>
        <p className="mt-1 font-display text-2xl font-semibold text-frost-50">
          {data.company_name}
        </p>
      </div>

      {/* hero animation (MP4 tickle panda recommended for MVP; image fallback) */}
      <div className="relative mx-auto mb-8 w-full max-w-sm">
        <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-aurora-band bg-[length:200%_200%] opacity-30 blur-2xl animate-aurora" />
        <div className="animate-float-slow rounded-[2.25rem] glass p-3 shadow-glass">
          <video
            className="w-full rounded-[1.75rem]"
            autoPlay
            loop
            muted
            playsInline
            poster="/images/image1.png"
          >
            {/* Drop a tickle-panda MP4 at public/media/tickle.mp4 to enable it. */}
            <source src="/media/tickle.mp4" type="video/mp4" />
          </video>
        </div>
      </div>

      <h1 className="font-display text-3xl font-semibold tracking-tight text-frost-50 sm:text-4xl">
        Your free Winter Panda
      </h1>
      <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-frost-200/80">
        {data.message ||
          "Thank you for your trust and support. As a token of our appreciation, we would like to send you a free Winter Panda."}
      </p>
      <p className="mt-3 text-sm text-frost-200/60">
        Download within {data.validity_days ?? 90} days and enjoy this desktop companion for the
        entire year.
      </p>

      {/* What they're being given (Outreach spec §8 step 3) */}
      <div className="mx-auto mt-8 grid max-w-lg gap-3 text-left sm:grid-cols-2">
        {[
          ["🐼", "A gentle desktop companion", "Hand-crafted animations that react to your day."],
          ["🎧", "Nature sounds", "Calming winter ambience you can switch on or off."],
          ["🔒", "Privacy-first", "No account, no tracking — signals stay on your device."],
          ["🪟", "Windows 10 & 11", "Lightweight, and quiet in the background."],
        ].map(([icon, title, body]) => (
          <div key={title} className="rounded-2xl glass px-4 py-3">
            <div className="text-lg">{icon}</div>
            <p className="mt-1 text-sm font-semibold text-frost-50">{title}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-frost-200/70">{body}</p>
          </div>
        ))}
      </div>

      {/* Legal acceptance gates the download (Outreach spec §8 step 4) */}
      <label className="mx-auto mt-8 flex max-w-lg cursor-pointer items-start gap-3 rounded-2xl glass px-5 py-4 text-left">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1 h-4 w-4 flex-none accent-aurora-cyan"
        />
        <span className="text-sm leading-relaxed text-frost-200/85">
          I have read and agree to the{" "}
          <a href="/#/legal/eula" className="text-aurora-cyan hover:text-frost-50">
            End User Licence Agreement
          </a>
          ,{" "}
          <a href="/#/legal/privacy" className="text-aurora-cyan hover:text-frost-50">
            Privacy Policy
          </a>{" "}
          and{" "}
          <a href="/#/legal/terms" className="text-aurora-cyan hover:text-frost-50">
            Terms of Use
          </a>
          .
        </span>
      </label>

      {agreed ? (
        <a
          href={data.download_url || "#"}
          onClick={() => markDownloaded(token)}
          className="mt-6 inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-9 py-4 text-base font-semibold text-midnight-900 shadow-cozy transition-transform hover:-translate-y-0.5"
        >
          Download My Winter Panda
        </a>
      ) : (
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Please accept the agreements above to continue"
          className="mt-6 inline-flex cursor-not-allowed items-center justify-center gap-2.5 rounded-full bg-white/10 px-9 py-4 text-base font-semibold text-frost-200/45"
        >
          Download My Winter Panda
        </button>
      )}

      {/* What happens next. The Windows warning is expected while the new signed
          installer builds download reputation, so recipients are told up front. */}
      <div className="mx-auto mt-9 max-w-md rounded-2xl glass p-5 text-left">
        <p className="text-xs font-medium uppercase tracking-[0.16em] text-frost-200/55">
          After downloading
        </p>
        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-frost-200/80">
          <li>Open <strong className="text-frost-100">WinterPanda-Setup.exe</strong> and follow the installer.</li>
          <li>
            If Windows shows &ldquo;Windows protected your PC&rdquo;, choose{" "}
            <strong className="text-frost-100">More info → Run anyway</strong>. The publisher is
            Lavina &amp; Tommy Inc, the US corporation behind Winter Desktops.
          </li>
          <li>Winter Panda appears on your desktop. No code or account needed.</li>
        </ol>
      </div>
    </div>
  );
}

function StateCard({
  title,
  body,
  footer,
}: {
  title: string;
  body: string;
  footer?: React.ReactNode;
}) {
  return (
    <div className="reveal is-visible w-full max-w-md rounded-[2rem] glass p-10 shadow-glass">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-2xl">
        🐼
      </div>
      <h1 className="font-display text-2xl font-semibold text-frost-50">{title}</h1>
      <p className="mt-3 text-sm leading-relaxed text-frost-200/75">{body}</p>
      {footer}
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-aurora-cyan" />
      <p className="text-sm text-frost-200/60">Loading your gift…</p>
    </div>
  );
}
